import { Link } from "apps/website/i18n/navigation";
import React from "react";
import type { Messages, RichTranslationValues } from "next-intl";
import type { NamespaceKeys, NestedKeyOf } from "use-intl";
import { TFunctionOf } from "apps/website/types/i18n";
import { asUntypedTranslator } from "./untypedTranslator";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import {
  TextBodyVariantsProps,
} from "apps/website/components/base/Text/TextBody/styles";

import { generateSignupHelpMailto, UserContactDetails } from "../misc/email";

const textBody = (chunks: React.ReactNode, size: TextBodyVariantsProps["size"] = 2) => <TextBody tag="span" size={size}>{ chunks }</TextBody>;

export const boldTextBody = (chunks: React.ReactNode): React.ReactNode => (
  <strong>
    { chunks }
  </strong>
);

export const createLinkText = (
  chunks: React.ReactNode,
  href: string,
  size?: TextBodyVariantsProps["size"],
  className?: string,
): React.ReactNode => (
  <Link className={`underline inline ${className || ""}`} href={href}>
    <TextBody className="inline" size={size} weight="bold">
      { chunks }
    </TextBody>
  </Link>
);

export const createEmailContactLink = (
  chunks: React.ReactNode,
  email = "meow@katkin.com",
  size: TextBodyVariantsProps["size"] = 1,
  weight: TextBodyVariantsProps["weight"] = "bold",
): React.ReactNode => (
  <Link className="underline inline" href={`mailto:${email}`}>
    <TextBody tag="span" className="inline" size={size} weight={weight}>
      { chunks }
    </TextBody>
  </Link>
);

export const createPrepopulatedEmailLink = (
  chunks: React.ReactNode,
  userDetails?: UserContactDetails,
  size: TextBodyVariantsProps["size"] = 1,
  weight: TextBodyVariantsProps["weight"] = "bold",
): React.ReactNode => {
  const mailtoLink = generateSignupHelpMailto(userDetails || {});

  return (
    <Link className="underline inline" href={mailtoLink}>
      <TextBody tag="span" className="inline" size={size} weight={weight}>
        { chunks }
      </TextBody>
    </Link>
  );
};

export const createLoginLink = (
  chunks: React.ReactNode,
  size: TextBodyVariantsProps["size"] = 2,
  weight: TextBodyVariantsProps["weight"] = "bold",
): React.ReactNode => (
  <Link className="underline inline" href="/login">
    <TextBody tag="span" size={size} weight={weight}>
      { chunks }
    </TextBody>
  </Link>
);

export const richCommon = {
  br: () => <br />,
  email: createEmailContactLink,
  strong: boldTextBody,
  link: createLinkText,
  textBody,
  span: (chunks: React.ReactNode) => <span>{ chunks }</span>,
};

export const renderRichTranslations = <
  NS extends NamespaceKeys<Messages, NestedKeyOf<Messages>>,
  T extends Record<string, unknown>,
>(
  t: TFunctionOf<NS>,
  key: string,
  extra?: T,
): React.ReactNode => {
  const richComponents = { ...richCommon, ...extra };

  return asUntypedTranslator(t).rich(key, richComponents as unknown as RichTranslationValues);
};
